import { useState } from "react";
import { CALENDAR_LIBRARY_CATEGORIES, SYSTEM_SCHEDULE_TEMPLATES, SYSTEM_SCHEDULE_ROOT_TEMPLATES, calendarLibraryCategory } from "../lib/trainingPlanUnits";
import { activityFields, runningPreset } from "../lib/runningTraining";
import { compileTrainingStructure, trainingPreset } from "../lib/trainingStructure";
import { calendarV2Copy } from "../lib/calendarV2Copy";
import { isRunningTrainingSelection, runningTrainingChoice } from "../lib/runningTrainingSelection";
import RunningVariantChooser from "./RunningVariantChooser";

export const editorLibraryCategory = (editor) => calendarLibraryCategory({ category: editor.category, training_unit_type: editor.trainingUnitType });

export function activitySelection(template, t, editor) {
  const patch = {
    ...activityFields(), category: template.category, trainingUnitType: template.unitType || null,
    recovery_type: template.recovery_type || null, appointment_type: template.appointment_type || null,
    matchType: template.matchType || "league",
    ...(template.unitType === "running_training" ? runningPreset(template.running_variant || "endurance_run", t) : {}),
    ...(template.training_template ? trainingPreset(template.training_template, t) : {}),
  };
  const previous = SYSTEM_SCHEDULE_TEMPLATES.find((row) => row.training_template && row.training_template === editor?.training_template);
  if (previous && previous.training_template !== template.training_template) {
    const generated = (value, key) => Object.values(calendarV2Copy).some((copy) => copy[key] === value);
    const description = editor.player_visible_description ?? editor.description;
    patch.title = generated(editor.title, previous.labelKey) ? patch.title ?? t(template.labelKey) : editor.title;
    patch.description = generated(description, previous.descriptionKey)
      ? patch.description ?? t(template.descriptionKey || template.labelKey) : description;
    const plan = compileTrainingStructure(editor.training_structure);
    const customDuration = plan.ok && plan.plan.totalDurationMs === null
      && Number.isFinite(Number(editor.duration)) && Number(editor.duration) > 0
      && Number(editor.duration) !== trainingPreset(previous.training_template, t).duration;
    patch.duration = customDuration && !template.training_template && patch.running_variant !== "interval_run" ? editor.duration
      : patch.duration ?? template.duration ?? (template.category === "match" ? 120 : "");
  }
  return patch;
}

export function runningActivitySelection(choice, t, editor) {
  if (runningTrainingChoice(editor) === choice && (editor.training_structure || editor.running_details)) return {};
  const template = SYSTEM_SCHEDULE_TEMPLATES.find((row) => row.training_template === choice)
    || { ...SYSTEM_SCHEDULE_TEMPLATES.find((row) => row.unitType === "running_training"), running_variant: choice };
  return activitySelection(template, t, editor);
}

export function scheduleActivityFields(editor) {
  const category = editor.trainingUnitType === "mobility" ? "training"
    : editor.trainingUnitType === "team_event" ? "appointment" : editor.category;
  return {
    ...activityFields(editor), category, training_unit_type: editor.trainingUnitType || null,
    gamepower_dimension: editor.trainingUnitType === "mobility" || category === "recovery" ? "recovery"
      : category === "training" ? "training" : null,
  };
}

export function libraryTemplateEditor(template) {
  const fields = activityFields(template);
  if (fields.running_details) delete fields.running_details.measured_hrmax;
  return {
    ...template, category: template.kind, trainingUnitType: template.training_unit_type || null,
    ...fields, matchType: template.match_type || "league",
    duration: template.default_duration_minutes ?? 0,
    default_time: template.default_time || "", active: template.active !== false,
    player_visible_description: template.player_visible_description || "",
  };
}

export function libraryTemplatePayload(editor) {
  const fields = scheduleActivityFields(editor);
  const category = fields.category;
  delete fields.category;
  delete fields.gamepower_dimension;
  if (fields.running_details) delete fields.running_details.measured_hrmax;
  return {
    ...fields, kind: category, title: editor.title.trim(),
    group_id: category === "training" ? editor.group_id || null : null,
    player_visible_description: editor.player_visible_description.trim(),
    default_duration_minutes: Number(editor.duration) || 0,
    default_time: editor.default_time || null, active: editor.active,
    match_type: category === "match" ? editor.matchType || "league" : null,
  };
}

export default function CalendarActivityFields({ editor, onChange, disabled = false, t, includeMatchSubtype = false }) {
  const [choosingRunning, setChoosingRunning] = useState(false);
  const category = editorLibraryCategory(editor);
  const choices = SYSTEM_SCHEDULE_ROOT_TEMPLATES.filter((template) => calendarLibraryCategory(template) === category);
  const selected = isRunningTrainingSelection(editor) ? choices.find((template) => template.unitType === "running_training")
    : choices.find((template) => template.unitType ? template.unitType === editor.trainingUnitType
    && (template.training_template || null) === (editor.training_template || null)
    : template.recovery_type ? template.recovery_type === editor.recovery_type
      : template.appointment_type ? template.appointment_type === editor.appointment_type
        : template.matchType === editor.matchType);
  return <>
    <label className="block"><span className="block text-[11px] uppercase tracking-[.12em] mb-1.5" style={{ color: "var(--pc-muted)" }}>{t("category")}</span>
      <select className="pc-input" data-testid="calendar-activity-category" disabled={disabled} value={category} onChange={(event) => {
        const template = SYSTEM_SCHEDULE_ROOT_TEMPLATES.find((row) => calendarLibraryCategory(row) === event.target.value);
        onChange(activitySelection(template, t, editor));
      }}>
        {CALENDAR_LIBRARY_CATEGORIES.map((kind) => <option key={kind} value={kind}>{t(`calendarCategory_${kind}`)}</option>)}
        {!CALENDAR_LIBRARY_CATEGORIES.includes(category) && <option value={category}>{t(`calendarCategory_${category}`) === `calendarCategory_${category}` ? editor.title : t(`calendarCategory_${category}`)}</option>}
      </select>
    </label>
    {choices.length > 0 && (category !== "match" || includeMatchSubtype) && <label className="block"><span className="block text-[11px] uppercase tracking-[.12em] mb-1.5" style={{ color: "var(--pc-muted)" }}>{t("calendarSubtype")}</span>
      <select className="pc-input" data-testid="calendar-activity-subtype" value={selected?.id || ""} disabled={disabled} onChange={(event) => {
        const template = choices.find((row) => row.id === event.target.value);
        if (template.unitType === "running_training") setChoosingRunning(true);
        else onChange(activitySelection(template, t, editor));
      }}>
        <option value="" disabled>{t("pleaseSelect")}</option>{choices.map((template) => <option key={template.id} value={template.id}>{t(template.labelKey)}</option>)}
      </select>
    </label>}
    {choosingRunning && <RunningVariantChooser date={editor.date} selectedChoice={runningTrainingChoice(editor)} t={t} onCancel={() => setChoosingRunning(false)}
      onChoose={(choice) => { onChange(runningActivitySelection(choice, t, editor)); setChoosingRunning(false); }} />}
  </>;
}
